import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#how-it-works', label: 'Jak to funguje' },
    { href: '#pricing', label: 'Ceník', testId: 'nav-pricing-link' },
    { href: '#faq', label: 'FAQ', testId: 'nav-faq-link' },
    { href: '#contact', label: 'Kontakt', testId: 'nav-contact-link' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        data-testid="top-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          isScrolled ? 'nav-blur border-b border-[var(--vp-line)]' : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a 
              href="/" 
              className="flex items-center gap-2 font-semibold text-[var(--vp-ink)]"
            >
              <div className="w-10 h-10 rounded-full bg-[var(--vp-ink)] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
                <div className="absolute w-2 h-2 bg-[var(--vp-pink)] rounded-full translate-x-2 -translate-y-2" />
              </div>
              <span className="text-lg" style={{ fontFamily: 'var(--font-display)' }}>
                Výzva přijata
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  data-testid={link.testId}
                  className="text-[var(--vp-ink)]/80 hover:text-[var(--vp-ink)] transition-colors text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('#pricing')}
                className="btn-accent px-4 py-2 text-sm rounded-full"
              >
                Koupit
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-[var(--vp-ink)]"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 mobile-menu-overlay md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 bottom-0 w-3/4 max-w-sm bg-white z-50 md:hidden shadow-xl"
          >
            <div className="p-6 pt-20">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="text-left text-[var(--vp-ink)] text-lg font-medium py-2 border-b border-[var(--vp-line)]"
                  >
                    {link.label}
                  </button>
                ))}
                <Button
                  onClick={() => scrollToSection('#pricing')}
                  className="btn-accent mt-4 w-full py-3 rounded-full"
                >
                  Koupit
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
